import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';

export const ChannelListRoot = styled(Stack, { label: 'ChannelListRoot' })(({ theme }) => ({
  width: 444,
  borderRight: `1px solid ${theme.palette.divider}`,
}));

export const GroupChannelListWrapper = styled(Box, {
  label: 'GroupChannelListWrapper',
  shouldForwardProp: (prop) => prop !== 'sharedScroll' && prop !== 'isCtx',
})<{ sharedScroll?: boolean; isCtx?: boolean }>(({ sharedScroll, isCtx }) => ({
  // Default: the list owns its own bounded scroll and fills the panel (flex:1). This is REQUIRED
  // while channels are still loading — Sendbird's infinite-scroll pagination listens on the inner
  // `.sendbird-channel-list__body` scroller, so it must stay a bounded scroll box.
  // Shared-scroll mode (DA-19646, current-context tab AFTER all channels are retrieved): the list
  // renders at CONTENT height and the outer PanelScrollArea provides the single scrollbar, so the
  // chat rows and the user directory below them scroll as one. Safe here ONLY because pagination
  // is already complete (nothing left to load), so neutralizing the inner scroller loses nothing.
  ...(sharedScroll ? { flexShrink: 0 } : { flex: 1, overflow: 'hidden' }),
  display: 'flex',
  flexDirection: 'column',
  '& .sendbird-channel-list__header': {
    display: 'none',
  },
  ...(sharedScroll
    ? {
        '& .sendbird-channel-list, & .sendbird-channel-list__body': {
          overflow: 'visible',
          height: 'auto',
        },
      }
    : {}),
  // Push timestamp to far right and keep the channel name truncated on the left.
  '& .sendbird-channel-preview__content__upper': {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
  },
  '& .sendbird-channel-preview__content__upper__header': {
    flex: '1 1 0',
    minWidth: 0,
    overflow: 'hidden',
  },
  '& .sendbird-channel-preview__content__upper__last-message-at': {
    flexShrink: 0,
    paddingLeft: 4,
  },
  // Sendbird hides the timestamp on hover to show the action button — keep it visible.
  '& .sendbird-channel-preview:hover .sendbird-channel-preview__content__upper__last-message-at': {
    display: 'block',
  },
  // Break long words/URLs without spaces to prevent overflow.
  '& .sendbird-channel-preview__content__lower__last-message': {
    wordBreak: 'break-all',
  },
  // Last message text should be high-contrast (black) when channel is active.
  '& .sendbird-channel-preview--active .sendbird-channel-preview__content__lower__last-message': {
    color: 'var(--sendbird-light-onlight-01)',
  },
  // Hide member count — not needed in this UI.
  '& .sendbird-channel-preview__content__upper__header__total-members': {
    display: 'none',
  },
  // Action container is replaced by our custom eye icon; hide it to prevent click interception.
  '& .sendbird-channel-preview__action': {
    display: 'none',
  },
  // Hide Sendbird's built-in NO_CHANNELS placeholder ONLY on the current-context list, where our
  // own EmptyChatsText is the single source of truth for the empty state (rendered by the panel as a
  // normal sibling with a divider). Sendbird would otherwise render its own placeholder (icon + "No
  // chats") when its collection is genuinely empty, producing a SECOND, differently-styled empty
  // block. On the "All" list we do NOT render EmptyChatsText, so Sendbird's placeholder is left
  // visible there to avoid a blank panel when the All collection is genuinely empty (DA-19646).
  ...(isCtx
    ? {
        '& .sendbird-place-holder': {
          display: 'none',
        },
      }
    : {}),
}));

export const FilterChipBar = styled(Box, { label: 'FilterChipBar' })(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(2),
  height: 48,
  flexWrap: 'wrap',
}));

export const FilterChip = styled(Chip, { label: 'FilterChip' })(({ theme }) => ({
  borderRadius: 8,
  fontSize: theme.typography.pxToRem(13),
  fontWeight: theme.typography.fontWeightRegular,
  '&.MuiChip-outlined': {
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
  },
  // Filled chips render no border by default, so a primary border must be set
  // explicitly (not just borderColor) to satisfy the "border in every state"
  // requirement.
  '&.MuiChip-filled': {
    border: `1px solid ${theme.palette.primary.main}`,
  },
  // The leading context icon (folder-open / receipt) inverts with the chip state
  // per Figma: primary-blue on the unselected/outlined chip, white on the
  // selected/filled chip. Both match the chip's own text color, so inheriting it
  // gives the inversion for free (outlined text = primary; filled-primary text =
  // contrast/white) without hardcoding either color.
  '& .MuiChip-icon': {
    color: 'inherit',
    fontSize: theme.typography.pxToRem(18),
  },
}));

export const UnreadPill = styled('span', {
  label: 'UnreadPill',
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected?: boolean }>(({ theme, selected }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  // minWidth + height + border-box keep a single-digit value (e.g. "1") a
  // perfect circle; multi-digit values expand horizontally into a pill.
  boxSizing: 'border-box',
  minWidth: 20,
  height: 20,
  borderRadius: 48,
  fontSize: theme.typography.pxToRem(12),
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: '20px',
  padding: theme.spacing(0, 0.8125),
  marginLeft: theme.spacing(0.75),
  backgroundColor: selected ? theme.palette.common.white : theme.palette.primary.main,
  color: selected ? theme.palette.primary.main : theme.palette.common.white,
}));

export const ChannelListSearchBox = styled(Box, { label: 'ChannelListSearchBox' })(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
}));

export const ChannelListFilterBox = styled(Box, { label: 'ChannelListFilterBox' })(({ theme }) => ({
  padding: theme.spacing(0, 2, 2, 2),
  backgroundColor: theme.palette.background.paper,
}));

export const ChatListHeader = styled(Box, { label: 'ChatListHeader' })(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: 64,
  minHeight: 64,
  boxSizing: 'border-box',
  padding: theme.spacing(2, 3),
  backgroundColor: theme.palette.common.white,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

export const ChannelListHeaderUsername = styled('span', { label: 'ChannelListHeaderUsername' })(({ theme }) => ({
  fontSize: theme.typography.pxToRem(18),
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: '24px',
  color: theme.palette.text.primary,
  display: 'flex',
  alignItems: 'center',
}));

// Inline section label rendered above the chat list ("Chats") and above the user directory
// ("Users") — DA-19646. Distinct from ChatListHeader (the panel's top bar): this is a lighter,
// smaller in-list separator that sits below the search box. `styled` (computed once) over `sx`
// because it renders inside the scrollable list region.
export const SectionHeader = styled('div', { label: 'SectionHeader' })(({ theme }) => ({
  padding: theme.spacing(1.5, 2, 1, 2),
  fontSize: theme.typography.pxToRem(15),
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: '20px',
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.background.paper,
}));

// Text-only "No chats" placeholder shown under the "Chats" header when the list is empty
// (DA-19646). Replaces Sendbird's PlaceHolder (type=NO_CHANNELS), which renders a large speech-
// bubble illustration not wanted in this panel — only the message is kept, styled to match the
// list's muted secondary text.
export const EmptyChatsText = styled('div', { label: 'EmptyChatsText' })(({ theme }) => ({
  padding: theme.spacing(2),
  fontSize: theme.typography.pxToRem(14),
  lineHeight: '20px',
  color: theme.palette.text.secondary,
  textAlign: 'center',
  // Same bottom divider as chat/user rows so the empty block separates the Chats and Users
  // sections identically to how individual rows are separated (DA-19646, task 6.2).
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

// Container for the user directory section (header + user rows) beneath the chat list. Content
// height; it lives inside the shared PanelScrollArea and scrolls together with the chat rows (it
// is only ever mounted once all channels are loaded and the panel is in shared-scroll mode).
export const UserDirectoryRoot = styled(Box, { label: 'UserDirectoryRoot' })({
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
});

// Single scroll region used on the current-context tab AFTER all channels are retrieved: the
// "Chats" header, the content-height chat list, the "Users" header and the user rows stack inside
// this one scroller so the whole panel scrolls as one continuous list (DA-19646). Before load
// completes this wrapper is inert (its child list keeps its own bounded scroll for pagination).
export const PanelScrollArea = styled(Box, {
  label: 'PanelScrollArea',
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ active }) => ({
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  // Only own the scroll once we've switched to shared-scroll mode; otherwise stay a passthrough so
  // the inner list's own scroll behaves exactly as before.
  overflowY: active ? 'auto' : 'hidden',
}));

// Round unread-count bubble. Used for the header total and for per-channel counts
// in the list rows (ChannelPreviewItem).
export const UnreadCountBadge = styled('span', { label: 'UnreadCountBadge' })(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 20,
  height: 20,
  borderRadius: 10,
  fontSize: theme.typography.pxToRem(12),
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: '20px',
  padding: theme.spacing(0, 0.75),
  marginLeft: theme.spacing(1),
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
}));
